import {Injectable} from '@angular/core';
import {AngularFireStorage} from '@angular/fire/compat/storage';
import {Observable, throwError} from 'rxjs';
import {TenantService} from './tenant.service';
import {AngularFireUploadTask} from '@angular/fire/compat/storage/task';
import {catchError, concatMap, last} from 'rxjs/operators';
import {UploadMetadata} from '@angular/fire/compat/storage/interfaces';
import {MessagesService} from '../shared/services/messages.service';
import {LoadingService} from '../shared/services/loading.service';
import {AngularFirestore} from '@angular/fire/compat/firestore';
import {isLifetimeSubscriber} from '../models/user.model';
import {Tenant} from '../models/tenant.model';
import {isAppSumoLimitedPlan} from '../common/appsumo-utils';


const CACHE_CONTROL_SETTINGS = 'public,max-age=25920000, s-maxage=25920000';

@Injectable()
export class FileUploadService {

  tenant:Tenant;

  constructor(
    private storage: AngularFireStorage,
    private tenantService: TenantService,
    private loading: LoadingService,
    private messages: MessagesService,
    private afs: AngularFirestore) {

    this.tenant = this.tenantService.tenant;

  }

  uploadFile(file: File, filePath: string, randomFileName = false, addThumbPrefix = false): AngularFireUploadTask {

    this.checkIfStorageSpaceExceeded(file);

    let uploadPath;

    if (addThumbPrefix) {
      uploadPath = filePath + '/thumb_' + file.name;
    } else {
      uploadPath = filePath + '/' + file.name;
    }

    if (randomFileName) {

      const extension = file.name.split('.').pop();

      uploadPath = uploadPath.replace(extension, this.afs.createId() + "." + extension);

    }

    return this.storage.upload(uploadPath, file, {cacheControl: CACHE_CONTROL_SETTINGS});
  }

  uploadFileWithNewName(file: File, filePath: string, fileName: string, downloadFileName:string = null): AngularFireUploadTask {

    this.checkIfStorageSpaceExceeded(file);

    return this.storage.upload(
      `${filePath}/${fileName}`,
      file,
      {
        cacheControl: CACHE_CONTROL_SETTINGS,
        contentDisposition: `attachment; filename=${downloadFileName ? downloadFileName :fileName};`,
      });
  }

  uploadProtectedResource(courseId: string, lessonId: string, file: File, fileName: string = null): AngularFireUploadTask {

    this.checkIfStorageSpaceExceeded(file);

    const uploadFileName = fileName ? fileName : this.generateUrlSafeUploadFileName(file);

    const uploadPath = `${this.tenant.id}/${courseId}/protectedResources/${lessonId}/${uploadFileName}`;

    return this.storage.upload(
      uploadPath,
      file, {
        cacheControl: CACHE_CONTROL_SETTINGS,
        contentDisposition: `attachment; filename=${file.name};`
      });

  }

  generateUrlSafeUploadFileName(file: File) {

    const extension = file.name.split('.').pop();

    return `${this.afs.createId()}.${extension}`;

  }

  getDownloadUrl(filePath: string): Observable<string> {
    return this.storage.ref(filePath).getDownloadURL();
  }

  deleteFile(filePath: string) {
    return this.storage.ref(filePath).delete();
  }

  uploadAndCreateUrl(storagePath: string, file: File, attachment = false) {

    this.checkIfStorageSpaceExceeded(file);

    const headers: UploadMetadata = {
      cacheControl: CACHE_CONTROL_SETTINGS
    };

    if (attachment) {
      headers.contentDisposition = `attachment; filename=${file.name};`
    }

    const uploadTask = this.storage.upload(`${storagePath}/${file.name}`, file, headers);

    const percentageUpload$ = uploadTask.percentageChanges();

    const upload$ = this.loading.showLoaderUntilCompleted(percentageUpload$);

    return upload$
      .pipe(
        last(),
        catchError(err => {
          console.log("Error uploading file, reason: ", err);
          this.messages.error('Could not upload file.');
          return throwError(err);
        }),
        concatMap(() => this.getDownloadUrl(`${storagePath}/${file.name}`)),
        catchError(err => {
          console.log("Error creating download url, reason: ", err);
          this.messages.error('File uploaded, but could not create download url.');
          return throwError(err);
        }),
      )
  }

  private checkIfStorageSpaceExceeded(file: File) {

    // storage space only applies to AppSumo lifetime users with version 2 or higher plans
    if (!isAppSumoLimitedPlan(this.tenant)) {
      return false;
    }

    const maxStorageInMb = this.tenant?.appsumoTierLimits?.maxStorage * 1024;

    const currentStorageInMb = this.tenant?.storageUsedInMb;

    const fileSizeInMb = file.size / 1024 / 1024;

    if (currentStorageInMb + fileSizeInMb > maxStorageInMb) {

      const maxStorageInGb = (maxStorageInMb / 1024).toFixed(2);

      this.messages.error(`You have exceeded your storage limit of ${maxStorageInGb} GB. Go to Admin / General to check your storage.`);

      throw new Error('Max storage limit exceeded.');

    }

  }
}
